import React, { useState, useEffect } from 'react';
import {
  Button,
  Form,
  Select,
  Spin,
  Image,
  Upload,
  Modal,
  Result,
  Alert,
  Input,
  Typography,
} from 'antd';
import { useSession } from 'next-auth/react';
import { InboxOutlined, LoadingOutlined } from '@ant-design/icons';
import {
  useCreateOneUserUploadedAssetsMutation,
  FindManyUserUploadedAssetsDocument,
} from '@/components/generated/graphql';
import buttons from '@components/ui-kit/buttons/submitButton.module.css';
import { ASSET_TYPE } from '@src/components/scene/react-three-components/sceneAsset';
import styles from './upload-asset.module.css';
import { v4 as uuidv4 } from 'uuid';

export enum FileType {
  IMAGE = 'IMAGE',
  MODEL = 'MODEL',
  AUDIO = 'AUDIO',
  VIDEO = 'VIDEO',
}

export enum FileStatus {
  READY = 'ready',
  UPLOADING = 'uploading',
  DONE = 'done',
  ERROR = 'error',
}

type FileObject = {
  originFileObj: File;
};

type ImageFile = {
  file: FileObject;
  fileList: FileObject[];
};

type FormValues = {
  name: string;
  assetFile: ImageFile;
  model_type_zip: 'fbx' | 'obj';
};

type FileUpload = null | string | ArrayBuffer;
type MessageType = string | null | undefined;
type AlertType = 'success' | 'error' | 'info' | undefined;
type Extension =
  | 'png'
  | 'jpeg'
  | 'jpg'
  | 'glb'
  | 'zip'
  | 'mp3'
  | 'mp4'
  | 'webm'
  | 'mov'
  | 'folder'
  | 'fbx'
  | 'obj'
  | '';

const { Dragger } = Upload;
const { Option } = Select;
const { Title, Text } = Typography;
const URL = process.env.NEXT_PUBLIC_ASSETS_IMAGES_URL;
const CONVERTER_API_URL = process.env.NEXT_PUBLIC_CONVERTER_API_URL;
const FILE_TYPE = 'assets';

const NOT_ALLOWED_EXTENSIONS = ['mtl'];
const MODEL_EXTENSIONS = ['glb', 'fbx', 'obj', 'zip'];
const GLTF_EXTENSIONS = ['gltf'];
const BINARY_EXTENSIONS = ['bin'];
const EXTENSIONS_REQUIRING_CONVERSION = ['fbx', 'obj', 'zip'];
const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg'];
const VIDEO_EXTENSIONS = ['mp4', 'mov', 'webm'];
const AUDIO_EXTENSIONS = ['mp3'];
const ASSET_ID_SUFFIX = 'StudioID';
const ACCEPTED_FILE_EXTENSIONS =
  '.zip, .bin, .fbx, .obj, .glb, .png, .jpg, .jpeg, .mp4, .webm, .mov, .mp3';

const generateUniqueFileName = (originalFileName: string): string => {
  const lastDotIndex = originalFileName.lastIndexOf('.');
  const nameWithoutExtension = originalFileName.slice(0, lastDotIndex);
  const extension = originalFileName.slice(lastDotIndex);
  const uniqueId = uuidv4();

  return `${nameWithoutExtension}_${uniqueId}${extension}`;
};

const uploadImage = async (file: File, creator: string, type: string) => {
  const data = new FormData();
  data.set('file', file);
  data.set('fileName', file.name);
  data.set('email', creator);
  data.set('fileType', type);

  const res = await fetch('/api/upload-file-s3', {
    method: 'POST',
    body: data,
  }).then((res) => res.json());

  if (!res?.status) throw new Error('Error when uploading image');
};

export function UploadAsset({
  userEmail,
  onCloseModal,
  callback,
  loading,
  setLoading,
}) {
  const { data: session } = useSession();
  const [message, setMessage] = useState<MessageType>(null);
  const [type, setType] = useState<AlertType>('error');
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [fileList, setFileList] = useState<any[]>([]);
  const [url, setUrl] = useState<FileUpload>('');
  const [createInventoryItem] = useCreateOneUserUploadedAssetsMutation();
  const [extension, setExtension] = useState<Extension>('');
  const [formProps] = Form.useForm();

  useEffect(() => {
    if (!URL || !CONVERTER_API_URL) {
      throw new Error(
        'Configuration error occurred, please contact Ozone tech support.'
      );
    }
  }, []);

  const updateFileProgress = (
    fileUid: string,
    percent: number,
    status?: FileStatus
  ) => {
    setFileList((prevList) =>
      prevList.map((file) =>
        file.uid === fileUid
          ? { ...file, percent, status: status || FileStatus.UPLOADING }
          : file
      )
    );
  };

  const onSubmit = async (values: FormValues) => {
    setLoading(true);
    setType('info');
    setMessage('Uploading your assets please wait...');

    try {
      const uploadPromises = fileList.map(async (file) => {
        const fileExtension = file.name.split('.').pop().toLowerCase();

        if (NOT_ALLOWED_EXTENSIONS.includes(fileExtension)) {
          updateFileProgress(file.uid, 100, FileStatus.ERROR);
          return null;
        }

        if (GLTF_EXTENSIONS.includes(fileExtension)) {
          updateFileProgress(file.uid, 100, FileStatus.ERROR);
          throw new Error(
            'GLTF files are not supported in this uploader. Please use "GLTF Package" from the menu for uploading GLTF models with their dependencies.'
          );
        }

        updateFileProgress(file.uid, 10, FileStatus.UPLOADING);

        let processedFile = file;
        let uniqueFileName: string;

        if (EXTENSIONS_REQUIRING_CONVERSION.includes(fileExtension)) {
          updateFileProgress(file.uid, 30, FileStatus.UPLOADING);

          if (!file.originFileObj) {
            updateFileProgress(file.uid, 100, FileStatus.ERROR);
            return null;
          }

          let formData = new FormData();
          formData.append('file', file.originFileObj);
          formData.append('category', values.model_type_zip);

          const res = await fetch(CONVERTER_API_URL as string, {
            method: 'POST',
            body: formData,
          }).then((res) => {
            if (res.status === 200) {
              return res.blob();
            }
            return null;
          });

          if (!res) {
            updateFileProgress(file.uid, 100, FileStatus.ERROR);
            throw new Error('Conversion failed');
          }

          updateFileProgress(file.uid, 60, FileStatus.UPLOADING);

          const convertedFileName = `${file.name.split('.')[0]}.glb`;
          uniqueFileName = generateUniqueFileName(convertedFileName);
          processedFile = new File([res], uniqueFileName);
        } else {
          if (!file.originFileObj) {
            updateFileProgress(file.uid, 100, FileStatus.ERROR);
            return null;
          }
          uniqueFileName = generateUniqueFileName(file.originFileObj.name);
          processedFile = new File([file.originFileObj], uniqueFileName);
        }

        updateFileProgress(file.uid, 80, FileStatus.UPLOADING);

        await uploadImage(processedFile, userEmail, FILE_TYPE);

        updateFileProgress(file.uid, 100, FileStatus.DONE);

        const assetURL = `${URL}/${encodeURIComponent(
          userEmail
        )}/${FILE_TYPE}/${encodeURIComponent(uniqueFileName)}`;

        let assetType;
        if (MODEL_EXTENSIONS.includes(fileExtension)) {
          assetType = ASSET_TYPE.GLTF.OTHER;
        } else if (BINARY_EXTENSIONS.includes(fileExtension)) {
          console.warn(
            'Binary file uploaded separately. Consider using GLTF Package for uploading with dependencies.'
          );
          return null;
        } else if (IMAGE_EXTENSIONS.includes(fileExtension)) {
          assetType = ASSET_TYPE.PRIMITIVE_TEXTURE.IMAGE_PLANE;
        } else if (VIDEO_EXTENSIONS.includes(fileExtension)) {
          assetType = ASSET_TYPE.PRIMITIVE_TEXTURE.VIDEO_PLANE;
        } else if (AUDIO_EXTENSIONS.includes(fileExtension)) {
          assetType = ASSET_TYPE.MISC.POSITIONAL_AUDIO;
        }

        const originalFileName = file.originFileObj?.name || file.name;
        const originalNameWithoutExt = originalFileName.split('.')[0];

        const assetName =
          fileList.length === 1 && values.name
            ? values.name + ASSET_ID_SUFFIX
            : originalNameWithoutExt + ASSET_ID_SUFFIX;

        return {
          type: assetType,
          url: assetURL,
          name: assetName.split(ASSET_ID_SUFFIX)[0],
          id: '',
          textureToChange: '',
          textureToChangeNormal: '',
          textureToChangeRoughness: '',
          textureToChangeMetalness: '',
        };
      });

      const settledResults = await Promise.allSettled(uploadPromises);

      settledResults.forEach((result) => {
        if (result.status === 'fulfilled' && result.value !== null) {
          callback(result.value);
          createInventoryItem({
            variables: {
              data: {
                assetType: result.value.type,
                assetUrl: result.value.url,
                name: result.value.name,
                userId: session?.user.id!,
              },
            },
            refetchQueries: [FindManyUserUploadedAssetsDocument],
          });
        }
      });

      setIsModalOpen(true);
    } catch (error) {
      console.log(error);
      setType('error');
      setMessage(
        "Something went wrong during the asset upload. Let's try again."
      );
    }

    setLoading(false);
  };

  const onClose = () => {
    onCloseModal();
  };

  /* Remove repeated files with less percentage when files are uploading */
  const filterSameFiles = (fileList: any[], files: any[]) => {
    let tempList = [...fileList, ...files];
    //filter files with less percentage but same id
    let uniqueFiles = {};
    tempList.forEach((file) => {
      if (uniqueFiles[file.uid]) {
        if ((uniqueFiles[file.uid].percent || 0) < (file.percent || 0))
          uniqueFiles[file.uid] = file;
      } else {
        uniqueFiles[file.uid] = file;
      }
    });

    tempList = Object.values(uniqueFiles);

    if (fileList.length === tempList.length) {
      return tempList;
    }
    return false;
  };

  /* Remove duplicates of file typed not allowed to be duplicated. Ex: 2 fbx */
  const removeDuplicates = (fileList: any[], files: any[]) => {
    const finalList = [...fileList, ...files];

    let extensions = ['.fbx', '.obj'];
    let counts = extensions.map((ext) =>
      finalList.reduce((acc, cur) => {
        return acc + Number(cur.name.includes(ext.toLowerCase()));
      }, 0)
    );

    //check if only one file per unique file type exists
    if (counts.some((count) => count > 1)) {
      return fileList;
    }

    //check if there are more than one type of 3D file
    if (counts.reduce((partialSum, a) => partialSum + a, 0) > 1) {
      return fileList;
    }

    //now check for only one completementary 3D file (.mtl or .bin)
    extensions = ['.mtl'];
    counts = extensions.map((ext) =>
      finalList.reduce((acc, cur) => {
        return acc + Number(cur.name.includes(ext.toLowerCase()));
      }, 0)
    );

    //check if only one file per unique file type exists
    if (counts.some((count) => count > 1)) {
      return fileList;
    }

    return finalList;
  };

  const hasFiles = fileList.length > 0;

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <Title level={3} className={styles.title}>
          Upload Asset
        </Title>

        <div className={styles.infoSection}>
          <Text type='secondary' className={styles.infoText}>
            Upload individual assets like images, audio and video files. For
            GLTF models with dependencies, use "GLTF Package" instead.
          </Text>
        </div>

        {message && (
          <Alert
            type={type}
            closable={true}
            message={message}
            showIcon
            style={{ marginBottom: 20 }}
          />
        )}

        <div className={styles.formContainer}>
          <Form
            className={styles.form}
            onFinish={onSubmit}
            layout='vertical'
            form={formProps}
          >
            <Form.Item
              label='Upload Asset'
              valuePropName='assetFile'
              name='assetFile'
              help={
                <div className={styles.helpTextBlock}>
                  <Text type='secondary' className={styles.helpText}>
                    File types supported: GLB, FBX, OBJ, ZIP, JPG, JPEG, MP4,
                    PNG, MP3
                    <br />
                    Maximum file size: 100mb
                    <br />
                    <strong>Note:</strong> For GLTF models, use "GLTF Package"
                    from the menu instead
                  </Text>
                </div>
              }
              className={styles.inputFields}
              rules={[{ required: true, message: 'Please upload file!' }]}
            >
              <Dragger
                openFileDialogOnClick={extension == '' && url == ''}
                multiple={true}
                className={styles.upload}
                accept={ACCEPTED_FILE_EXTENSIONS}
                height={300}
                fileList={fileList}
                showUploadList={{
                  showPreviewIcon: false,
                  showRemoveIcon: true,
                  showDownloadIcon: false,
                }}
                onChange={(info) => {
                  let reader = new FileReader();
                  let file = info.file;
                  let files = info.fileList;

                  let finalList: any = filterSameFiles(fileList, files);

                  if (!finalList) {
                    finalList = removeDuplicates(fileList, files);
                  }

                  const readyFileList = finalList.map((fileItem: any) => ({
                    ...fileItem,
                    status: FileStatus.READY,
                    percent: 0,
                  }));

                  setFileList(readyFileList);

                  if (info.fileList.length === 0) {
                    setExtension('');
                    setUrl('');
                  } else if (info.fileList.length > 1) {
                    setExtension('folder');
                  } else {
                    if (file.originFileObj) {
                      const extensionIndex =
                        file.originFileObj.name.lastIndexOf('.');
                      const name = file.originFileObj.name.slice(
                        0,
                        extensionIndex
                      );
                      const extension = file.originFileObj.name.slice(
                        extensionIndex + 1
                      );

                      formProps.setFieldValue('name', name);
                      setExtension(extension.toLowerCase() as Extension);
                      reader.readAsDataURL(file.originFileObj);
                      reader.onload = function () {
                        setUrl(reader.result);
                      };
                    }
                  }
                }}
              >
                <div className={styles.uploadContent}>
                  <p className='ant-upload-drag-icon'>
                    <InboxOutlined />
                  </p>
                  <p className='ant-upload-text'>
                    Click or drag files to this area to upload
                  </p>
                  <p className='ant-upload-hint'>
                    Support for images, audio and video files
                  </p>
                </div>
              </Dragger>
            </Form.Item>

            {extension === 'zip' && (
              <Form.Item
                label='Model type for zip upload'
                name='model_type_zip'
                help={
                  <span className={styles.help}>
                    Please select the model extension for the zip file.
                  </span>
                }
                className={styles.inputFields}
                rules={[
                  { required: true, message: 'Please input Model Type!' },
                ]}
              >
                <Select className={styles.selectModelType} bordered={false}>
                  <Option value={'fbx'}>.fbx</Option>
                  <Option value={'obj'}>.obj</Option>
                </Select>
              </Form.Item>
            )}

            {fileList.length === 1 && (
              <Form.Item
                label='Asset Name'
                name='name'
                className={styles.inputForm}
                rules={[
                  { required: true, message: 'Please input Asset Name!' },
                ]}
              >
                <Input className={styles.input} />
              </Form.Item>
            )}

            <Button
              htmlType='submit'
              type='primary'
              disabled={loading || !hasFiles}
              className={buttons.primaryButton}
              style={{
                width: 200,
                alignSelf: 'center',
                marginTop: 20,
              }}
            >
              {loading ? (
                <Spin
                  indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                />
              ) : (
                <span style={{ color: '#fff' }}>Upload Asset</span>
              )}
            </Button>
          </Form>

          <Modal
            open={isModalOpen}
            title={false}
            footer={false}
            onCancel={onClose}
            zIndex={1010}
          >
            <Result
              status='success'
              title='Successfully uploaded Asset!'
              extra={[
                <Button
                  type='primary'
                  key='ok'
                  className={buttons.primaryButton}
                  onClick={onClose}
                >
                  <span style={{ color: '#fff' }}>Close</span>
                </Button>,
              ]}
            />
          </Modal>
        </div>
      </div>
    </div>
  );
}
